// @ts-nocheck

import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import { SPORTS_MENU_LIST_ITEM_BORDER_RADIUS, SPORTS_MENU_LIST_ITEM_HEIGHT } from '../../constants/ui/sportsMenu';
import { getAppDOMElement } from '../../DOMElements';
import useSportsMenuContext from '../../hooks/useSportsMenuContext';
import useSportsMenuScrollContext from '../../hooks/useSportsMenuScrollContext';
import { SportListItemTypes, type SportsMenuItem } from '../../states/sportsmenu';
import theme from '../../styled-components/dark';
import { withOpacity } from '../../styled-components/dark/helpers';
import { SportsMenuTitleLabel } from '../../styled-components/shared';
import CollapseListItem from '../CollapseListItem';
import ExpandArrow from '../ExpandArrow';
import ListItem, { ListWrapper } from '../ListItem';
import SportsMenuSportsListItem from '../SportsMenuSportsListItem';
import SportsMenuTopCompetitions from '../SportsMenuTopCompetitions';

const PADDING_SIZE = 1;
const DEFAULT_PADDING = 0.5;

const StyledListWrapper = styled(ListWrapper)`
  background-color: ${({ $background }) => ($background ? theme.SportsMenuListItem.backgroundColor : 'transparent')};
  border-radius: ${SPORTS_MENU_LIST_ITEM_BORDER_RADIUS}px;
`;

const StyledListItem = styled(ListItem)`
  background-image: none;
  height: ${SPORTS_MENU_LIST_ITEM_HEIGHT}em;
  border-top: none;
  padding: 0 0.5em;

  ${Target.__MOBILE__
    ? css`
        letter-spacing: normal;
      `
    : undefined}

  padding-left: ${({ $padding }) => ($padding ? `${$padding * 2}em` : undefined)};

  &:hover {
    ${Target.__MOBILE__
      ? undefined
      : css`
          background-color: ${theme.SportsMenuListItem.hoverColor};
          filter: brightness(110%);
          box-shadow: none;
        `}
  }

  &:active {
    background-color: ${theme.SportsMenuListItem.active.backgroundColor};
    filter: brightness(100%);
  }
`;

const RowWrapper = styled.div`
  background-color: ${({ $isOpened }) =>
    $isOpened ? theme.SportsMenuListItem.extended.backgroundColor : 'transparent'};
`;

const StyledTopCompetitions = styled(SportsMenuTopCompetitions)``;

const StyledSeeAllArrow = styled(ExpandArrow)`
  position: absolute;
  right: 0.7em;
  height: 1em;
  width: 1em;
  transition: transform 150ms ease-in-out;
  transform: rotate(90deg);

  & .sports-arrow-inner-group {
    stroke: ${theme.SportsMenuListItem.arrowColor};
  }
`;

const ListItemLabel = styled.div`
  position: relative;
  flex: 1;
  height: 100%;
  display: flex;
  align-items: center;

  ${({ $border, $margin, $nextIsOpen }) => css`
    ${$margin
      ? css`
          margin-left: 2.8em;
        `
      : undefined}
    ${$border
      ? css`
          &:after {
            content: '';
            position: absolute;
            left: 0;
            bottom: 0;
            height: 1px;
            width: 100%;
            z-index: 1;
            background-color: ${$nextIsOpen ? 'transparent' : theme.SportsMenuListItem.separatorColor};
          }
        `
      : undefined}
  `}
`;

const StyledTitleLabel = styled(SportsMenuTitleLabel)`
  padding-left: 0.5em;

  ${({ $depth = 0 }) => {
    const borders = $depth
      ? css`
          padding-left: ${$depth * 2}em;
          border-bottom: 1px solid ${withOpacity(theme.BasketCombinationsSelectorLine.separatorBackgroundColor, '8a')};
          border-top: 1px solid ${withOpacity(theme.BasketCombinationsSelectorLine.separatorBackgroundColor, '8a')};
        `
      : undefined;
    return css`
      padding-left: ${$depth * PADDING_SIZE + DEFAULT_PADDING}em;
      ${borders}
    `;
  }}
`;

type OnExpandClickType = (key: string | null) => void;

type RowTypes = {
  row: SportsMenuItem;
  idx: number;
  depth: number;
  onExpandClick: OnExpandClickType;
  count: number;
  isOpened: boolean;
  nextIsOpen: boolean;
};

const Row = React.memo<RowTypes>(function Row({
  row,
  idx,
  depth,
  onExpandClick,
  count,
  isOpened,
  nextIsOpen,
}: RowTypes) {
  const { setScrollPositions } = useSportsMenuScrollContext();
  const location = useLocation();
  const scrollElement = getAppDOMElement();

  const onExpandItemClick = useCallback(
    (expandId) => () => {
      onExpandClick(expandId);
    },
    [onExpandClick],
  );

  const saveScrollPosition = useCallback(() => {
    if (Target.__MOBILE__ && setScrollPositions) {
      setScrollPositions((prev) => ({ ...prev, [location.pathname]: scrollElement.scrollTop }));
    }
  }, [location, scrollElement, setScrollPositions]);

  const onClickConfig = useMemo(
    () =>
      row.expandable
        ? {
            onClick: onExpandItemClick(row.expandId, !isOpened),
          }
        : {
            onClick: saveScrollPosition,
            to: row.to,
          },
    [row.expandable, row.expandId, row.to, onExpandItemClick, isOpened, saveScrollPosition],
  );

  return (
    <RowWrapper $isOpened={isOpened}>
      <SportsMenuSportsListItem
        depth={depth}
        isOpened={isOpened}
        nextIsOpen={nextIsOpen}
        label={row.label}
        sportId={row.sportId}
        flagId={row.flagId}
        expandable={row.expandable}
        showNewTag={row.showNewTag}
        onClickConfig={onClickConfig}
        border={idx !== count - 1 || isOpened}
      />
      {row.expandable ? (
        <CollapseListItem key={`${row.id}-collapse`} isOpened={isOpened}>
          {row.content?.map((item, idx) => {
            let nextId = '';
            if (row.content && row.content.length > idx - 2) {
              nextId = row.content[idx + 1]?.expandId ?? '';
            }
            return (
              <Item
                key={`${item.type}-${item.label}-${idx}`}
                item={item}
                idx={idx}
                depth={depth}
                extraDepth={1}
                onExpandClick={onExpandClick}
                count={row.content?.length ?? 0}
                padding={!!row.sportId || !!row.flagId}
                nextExpandId={nextId}
              />
            );
          })}
        </CollapseListItem>
      ) : null}
    </RowWrapper>
  );
});

type ItemTypes = {
  item: SportsMenuItem;
  idx: number;
  depth: number;
  extraDepth?: number;
  onExpandClick: OnExpandClickType;
  count: number;
  nextExpandId?: string;
  isOpened?: boolean;
  nextIsOpen?: boolean;
};

const Item = React.memo(function Item({
  item,
  idx,
  depth,
  extraDepth = 0,
  onExpandClick,
  count,
  nextExpandId = '',
  isOpened = false,
  nextIsOpen = false,
}: ItemTypes) {
  const { setScrollPositions } = useSportsMenuScrollContext();
  const location = useLocation();
  const scrollElement = getAppDOMElement();

  const saveScrollPosition = useCallback(() => {
    if (Target.__MOBILE__ && setScrollPositions) {
      setScrollPositions((prev) => ({ ...prev, [location.pathname]: scrollElement.scrollTop }));
    }
  }, [location, scrollElement, setScrollPositions]);

  const SeeAllButton = useMemo(
    () => (
      <StyledListItem key={item.id} to={item.to} $padding={depth + extraDepth} onClick={saveScrollPosition}>
        <ListItemLabel $border={idx + extraDepth} $margin={depth + extraDepth !== 0}>
          {item.label}
        </ListItemLabel>
        <StyledSeeAllArrow className="see-all-btn" />
      </StyledListItem>
    ),
    [item.id, idx, item.to, depth, extraDepth, item.label, saveScrollPosition],
  );

  const TopCompetitionList = useMemo(
    () => (
      <StyledTopCompetitions
        padHeader
        key={item.id}
        items={item.content}
        showSportIcon={false}
        depth={depth + extraDepth}
      />
    ),
    [item.id, item.content, depth, extraDepth],
  );

  const CompetitionList = useMemo(
    () => (
      <SportsMenuSportsList content={item.content} onExpandClick={onExpandClick} depth={depth + 1} noBorderRadius />
    ),
    [depth, item.content, onExpandClick],
  );

  const RenderedLabel = useMemo(
    () => (
      <StyledTitleLabel $pad key={item.id}>
        {item.label}
      </StyledTitleLabel>
    ),
    [item.id, item.label],
  );

  const RenderedList = useMemo(
    () => <SportsMenuSportsList key={item.id} content={item.content} depth={depth + 1} onExpandClick={onExpandClick} />,
    [depth, item.content, item.id, onExpandClick],
  );

  const RenderedRow = useMemo(
    () => (
      <Row
        row={item}
        idx={idx}
        depth={depth + extraDepth}
        onExpandClick={onExpandClick}
        count={count}
        isOpened={isOpened}
        nextIsOpen={nextIsOpen}
        nextExpandId={nextExpandId}
      />
    ),
    [count, depth, extraDepth, idx, isOpened, item, nextExpandId, nextIsOpen, onExpandClick],
  );

  switch (item.type) {
    case SportListItemTypes.SEE_ALL:
      return SeeAllButton;
    case SportListItemTypes.TOP_COMPETITIONS:
      return TopCompetitionList;
    case SportListItemTypes.COMPETITIONS:
      return CompetitionList;
    case SportListItemTypes.HEADER:
      return RenderedLabel;
    case SportListItemTypes.LIST:
      return RenderedList;
    case SportListItemTypes.ROW:
      return RenderedRow;
    default:
      return null;
  }
});

type SportsMenuSportsListProps = {
  content: Array<SportsMenuItem> | null;
  depth?: number;
  onExpandClick: OnExpandClickType;
  noBorderRadius?: boolean;
};

/* Recursive component, SportsMenuSportsList -> Item -> SportsMenuSportsList */
const SportsMenuSportsList = React.memo<SportsMenuSportsListProps>(function SportsMenuSportsList({
  content,
  depth = 0,
  onExpandClick,
  noBorderRadius = false,
}: SportsMenuSportsListProps) {
  // Si il n'y a qu'un seul item, on le déplie automatiquement uniquement sur mobile
  const { openKeys } = useSportsMenuContext();

  useEffect(
    () => {
      if (Target.__MOBILE__) {
        const expandableItems = content?.filter((c) => c.expandable);

        if (expandableItems?.length === 1 && openKeys?.indexOf(expandableItems?.[0].expandId) === -1) {
          onExpandClick(expandableItems?.[0].expandId);
        }
      }
    },
    // Call useEffect uniquement quand content et onExpandClick sont mis à jour (une fois, changement de page dans le menu)
    // Ingore pour openKeys (à chaque fois qu'on expand une section)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [content, onExpandClick],
  );

  if (content) {
    return (
      <StyledListWrapper $background={depth === 0} $border={depth === 0 && !noBorderRadius}>
        {content.map((item, idx) => {
          let nextId = '';
          if (content.length > idx - 2) {
            nextId = content[idx + 1]?.expandId ?? '';
          }

          const isOpened = openKeys?.indexOf(item.expandId) !== -1;
          const nextIsOpen = openKeys?.indexOf(nextId) !== -1;

          return (
            <Item
              key={item.id}
              item={item}
              idx={idx}
              depth={depth}
              onExpandClick={onExpandClick}
              count={content.length}
              nextExpandId={nextId}
              isOpened={isOpened}
              nextIsOpen={nextIsOpen}
            />
          );
        })}
      </StyledListWrapper>
    );
  }
  return null;
});

export default SportsMenuSportsList;
